<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGameState } from '@/stores/gameState'
import PotChipStack from './chips/PotStack.vue'
import { playEvent } from '@/helpers/sound'

const { roomState, player, winners } = storeToRefs(useGameState())

let deltaLeft = 0
let deltaTop = 0

function beforeLeave(el: Element) {
  let currentNode = el.querySelector('.chip-stack__stack-chip')! as HTMLElement
  let currentLeft = 0
  let currentTop = 0

  if (!currentNode) {
    return
  }

  const id = el.getAttribute('data-id')!
  if (winners.value.length === 0) {
    return
  }
  const playerId = winners.value[0]

  if (playerId.toString() === player.value?.playerId.toString()) {
    playEvent('winner')
  }

  const target = document.querySelector(`[data-id="${playerId}"]`)! as HTMLElement

  if (!target) {
    return
  }

  const {
    offsetParent: targetOffsetParent,
    offsetLeft: targetOffsetLeft,
    offsetTop: targetOffsetTop
  } = target

  while (currentNode !== targetOffsetParent) {
    currentLeft += currentNode.offsetLeft
    currentTop += currentNode.offsetTop
    currentNode = currentNode.offsetParent as HTMLElement
    if (!currentNode) {
      return
    }
  }

  deltaLeft = currentLeft - targetOffsetLeft
  deltaTop = currentTop - targetOffsetTop
  ;[...el.querySelectorAll('.chip-stack__stack-chip')].forEach((rawEl: Element) => {
    const currentEl = rawEl as HTMLElement
    currentEl.style.left = `${currentEl.offsetLeft}px`
    currentEl.style.top = `${currentEl.offsetTop}px`
    currentEl.style.opacity = '1'
  })

  return undefined
}

function onLeave(el: Element, done: () => void) {
  const chips = [...el.querySelectorAll('.chip-stack__stack-chip')]
  if (!chips.length || (deltaLeft === 0 && deltaTop === 0)) {
    done()
    return
  }

  chips.forEach((rawEl: Element) => {
    const currentEl = rawEl as HTMLElement
    currentEl.style.left = `${currentEl.offsetLeft - deltaLeft}px`
    currentEl.style.top = `${currentEl.offsetTop - deltaTop}px`
    currentEl.style.opacity = '0'
    el.addEventListener('transitionend', done)
  })
}

const pots = computed(() => roomState.value.hand?.pots ?? [])
</script>
<template>
  <div>
    <div
      tag="div"
      name="table__pots"
      class="table__pots-row"
      @before-leave="beforeLeave"
      @leave="onLeave"
    >
      <template v-for="(pot, idx) in pots" :key="pot.potId">
        <pot-chip-stack
          :value="pot.amount"
          class="table__pot"
          :data-id="pot.potId"
          layout="hirizontal-reverse"
          variant="light"
        />
        <div v-if="pots[idx + 1]" class="table__pot-separator" />
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.chip-stack {
  &__stack-chip {
    &:nth-child(1) {
      bottom: 0;
      transition-delay: 0;
    }

    &:nth-child(2) {
      bottom: 50%;
      transition-delay: 200ms;
    }

    &:nth-child(3) {
      bottom: 100%;
      transition-delay: 500ms;
    }
  }
}
</style>
